import React, { useState, useEffect } from 'react';
import { Modal, Input, Checkbox, Tooltip, Tag } from 'antd';
import {
    CheckCircleOutlined,
    InfoCircleOutlined,
    FileTextOutlined,
    CheckSquareOutlined,
    ExclamationCircleOutlined,
    WarningOutlined,
} from '@ant-design/icons';

const { TextArea } = Input;

export default function MilestoneCompletionModal({
    open,
    onClose,
    onConfirm,
    loading = false,
    milestone = {},
}) {
    const [comments, setComments] = useState('');
    const [checklist, setChecklist] = useState([]);

    const title = milestone.title || milestone.name || 'Milestone';
    const projectName = milestone.project?.name || milestone.projectName || 'Project';
    const totalChildTasks = milestone.totalChildTasks || 0;
    const completedChildTasks = milestone.completedChildTasks || 0;
    const remainingChildTasks = milestone.remainingChildTasks || 0;
    const progress = milestone.progress ?? (totalChildTasks > 0 ? Math.round((completedChildTasks / totalChildTasks) * 100) : 0);

    // Initialize checklist when modal opens or milestone changes
    useEffect(() => {
        if (!open) return;
        setComments('');

        if (Array.isArray(milestone.deliverableChecklist) && milestone.deliverableChecklist.length > 0) {
            setChecklist(
                milestone.deliverableChecklist.map((item, idx) => ({
                    deliverableId: item.deliverableId || item.creDeliverableId || `DELIV-${idx + 1}`,
                    creDeliverableId: item.creDeliverableId || item.deliverableId || '',
                    erpChecklistItemId: item.erpChecklistItemId || (item._id ? String(item._id) : ''),
                    title: item.title || item.name || 'Deliverable',
                    order: item.order !== undefined ? item.order : idx + 1,
                    completed: Boolean(item.completed),
                    completedAt: item.completedAt || null,
                    completedBy: item.completedBy || '',
                    rejected: Boolean(item.rejected),
                    rejectionNote: item.rejectionNote || '',
                }))
            );
        } else if (Array.isArray(milestone.deliverables) && milestone.deliverables.length > 0) {
            setChecklist(
                milestone.deliverables.map((item, idx) => ({
                    deliverableId: `DELIV-${idx + 1}`,
                    creDeliverableId: '',
                    erpChecklistItemId: '',
                    title: item.title || item.name || 'Deliverable',
                    order: idx + 1,
                    completed: Boolean(item.done || item.completed),
                    completedAt: null,
                    completedBy: '',
                    rejected: false,
                    rejectionNote: '',
                }))
            );
        } else {
            setChecklist([]);
        }
    }, [open, milestone]);

    const handleToggleItem = (index) => {
        setChecklist((prev) =>
            prev.map((item, i) => {
                if (i !== index) return item;
                const nextCompleted = !item.completed;
                return {
                    ...item,
                    completed: nextCompleted,
                    // If re-verifying an item that was rejected, clear the rejection flag
                    rejected: nextCompleted ? false : item.rejected,
                    completedAt: nextCompleted ? new Date().toISOString() : null,
                };
            })
        );
    };

    const handleSelectAll = (checkAll) => {
        setChecklist((prev) =>
            prev.map((item) => ({
                ...item,
                completed: checkAll,
                rejected: checkAll ? false : item.rejected,
                completedAt: checkAll ? new Date().toISOString() : null,
            }))
        );
    };

    const handleOk = () => {
        // Ensure all verified items reflect completed status
        const finalizedChecklist = checklist.map((item) => ({
            ...item,
            completed: Boolean(item.completed),
            completedAt: item.completed ? (item.completedAt || new Date().toISOString()) : null,
        }));
        onConfirm(comments, finalizedChecklist);
    };

    const totalDeliverables = checklist.length;
    const completedDeliverables = checklist.filter((item) => item.completed).length;
    const rejectedDeliverables = checklist.filter((item) => item.rejected).length;
    const allChecked = totalDeliverables > 0 && completedDeliverables === totalDeliverables;
    const canComplete = totalDeliverables === 0 || allChecked;
    const isCompleteDisabled = loading || !canComplete;

    return (
        <Modal
            open={open}
            onCancel={onClose}
            onOk={handleOk}
            confirmLoading={loading}
            footer={null}
            width={640}
            centered
            className="clean-milestone-completion-modal"
        >
            <div className="p-3 sm:p-5 space-y-5 text-base text-[#191c1d] dark:text-white">
                {/* Header */}
                <div className="flex items-center gap-3.5">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-amber-500 to-amber-300 flex items-center justify-center shrink-0 shadow-md shadow-amber-500/20">
                        <span className="text-white font-black text-2xl">✓</span>
                    </div>
                    <div>
                        <h3 className="text-2xl font-black text-[#03488a] dark:text-[#a7c8ff]">
                            Complete Milestone &amp; Sync to CRE
                        </h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                            Verify and check off all deliverables before pushing completion to the CRE client portal.
                        </p>
                    </div>
                </div>

                {/* Main Milestone Card */}
                <div className="bg-[#fffbeb] dark:bg-[#1a1710] p-5 rounded-2xl border border-amber-300 dark:border-amber-800 space-y-4 shadow-sm">
                    <div className="flex justify-between items-start gap-4">
                        <div>
                            <span className="text-xs font-black text-amber-800 dark:text-amber-400 uppercase tracking-wider">
                                Milestone
                            </span>
                            <h4 className="font-extrabold text-xl text-[#191c1d] dark:text-white flex items-center gap-2 mt-1">
                                <span className="text-amber-500 font-black text-2xl">◆</span> {title}
                            </h4>
                        </div>
                        <span className="text-sm font-bold px-3.5 py-1.5 rounded-xl bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300 border border-blue-200 dark:border-blue-800 shrink-0">
                            Project: {projectName}
                        </span>
                    </div>

                    <div className="flex items-center justify-between text-sm pt-3 border-t border-amber-200/80 dark:border-amber-900/60">
                        <span className="text-amber-900 dark:text-amber-300 font-bold">Child Tasks Progress:</span>
                        <span className="font-black text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950 px-3 py-1 rounded-lg border border-emerald-300 dark:border-emerald-800 text-sm">
                            {progress}% ({completedChildTasks}/{totalChildTasks} Tasks)
                        </span>
                    </div>

                    {remainingChildTasks > 0 && (
                        <div className="bg-amber-100/80 dark:bg-amber-950/70 p-3.5 rounded-xl border border-amber-300 dark:border-amber-800 flex items-center gap-3 text-amber-950 dark:text-amber-200 text-sm font-semibold">
                            <InfoCircleOutlined className="text-amber-600 text-base shrink-0" />
                            <span>Warning: {remainingChildTasks} child task{remainingChildTasks > 1 ? 's are' : ' is'} still incomplete.</span>
                        </div>
                    )}
                </div>

                {/* Completion Criteria */}
                {milestone.completionCriteria && (
                    <div className="space-y-2">
                        <span className="text-xs font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1.5 uppercase tracking-wider">
                            <FileTextOutlined className="text-[#03488a] text-sm" /> Completion Criteria
                        </span>
                        <div className="bg-slate-50 dark:bg-slate-900/80 p-3.5 rounded-xl text-sm text-slate-800 dark:text-slate-200 italic border-l-4 border-[#03488a] leading-relaxed">
                            "{milestone.completionCriteria}"
                        </div>
                    </div>
                )}

                {/* Interactive Deliverables Checklist */}
                <div className="space-y-2.5">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <CheckSquareOutlined className="text-[#03488a] text-base" />
                            <span className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                                Milestone Deliverables Checklist
                            </span>
                            {totalDeliverables > 0 && (
                                <Tag color={allChecked ? 'success' : 'processing'} className="font-bold rounded-md">
                                    {completedDeliverables}/{totalDeliverables} Verified
                                </Tag>
                            )}
                            {rejectedDeliverables > 0 && (
                                <Tag color="error" className="font-bold rounded-md">
                                    {rejectedDeliverables} Rejected by BD
                                </Tag>
                            )}
                        </div>

                        {totalDeliverables > 0 && (
                            <button
                                type="button"
                                onClick={() => handleSelectAll(!allChecked)}
                                className="text-xs font-bold text-[#03488a] dark:text-[#a7c8ff] hover:underline cursor-pointer"
                            >
                                {allChecked ? 'Deselect All' : 'Check All Items'}
                            </button>
                        )}
                    </div>

                    {totalDeliverables === 0 ? (
                        <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-sm text-slate-500 dark:text-slate-400 italic text-center">
                            No itemized deliverable checklist found for this milestone. All milestone criteria will be pushed as verified.
                        </div>
                    ) : (
                        <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                            {checklist.map((item, idx) => (
                                <div
                                    key={idx}
                                    onClick={(e) => {
                                        if (e.target.tagName === 'INPUT' || e.target.closest('.ant-checkbox-wrapper')) return;
                                        handleToggleItem(idx);
                                    }}
                                    className={`p-3.5 rounded-xl border-2 transition-all cursor-pointer flex items-start gap-3 select-none ${
                                        item.completed
                                            ? 'bg-emerald-50/80 dark:bg-emerald-950/25 border-emerald-400 dark:border-emerald-700/80 shadow-xs'
                                            : item.rejected
                                            ? 'bg-rose-50/80 dark:bg-rose-950/30 border-rose-300 dark:border-rose-800'
                                            : 'bg-white dark:bg-[#1a1a1a] border-slate-200 dark:border-slate-800 hover:border-blue-400 shadow-2xs'
                                    }`}
                                >
                                    <div className="pt-0.5">
                                        <Checkbox
                                            checked={item.completed}
                                            onChange={() => handleToggleItem(idx)}
                                            className="text-emerald-600"
                                        />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span
                                                className={`text-sm font-semibold truncate ${
                                                    item.completed
                                                        ? 'line-through text-slate-500 dark:text-slate-400'
                                                        : 'text-slate-800 dark:text-slate-100'
                                                }`}
                                            >
                                                {item.title}
                                            </span>
                                            {item.deliverableId && !item.deliverableId.match(/^[0-9a-fA-F]{24}$/) && (
                                                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 shrink-0">
                                                    {item.deliverableId}
                                                </span>
                                            )}
                                        </div>

                                        {/* Rejection Notice if rejected by BD */}
                                        {item.rejected && (
                                            <div className="mt-1.5 flex items-start gap-1.5 text-xs text-rose-700 dark:text-rose-300 bg-rose-100/70 dark:bg-rose-950/70 p-2 rounded-lg border border-rose-200 dark:border-rose-800">
                                                <WarningOutlined className="text-rose-500 mt-0.5 shrink-0" />
                                                <div>
                                                    <span className="font-bold">Rejected by BD on CRE: </span>
                                                    <span>{item.rejectionNote || 'Changes requested. Please verify updates.'}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Completion Comments */}
                <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                        Completion Comments (Optional):
                    </label>
                    <TextArea
                        rows={2}
                        placeholder="Provide any final approval notes or comments..."
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        className="rounded-xl text-sm p-3"
                    />
                </div>

                {/* Deliverables Verification Live Status Banner */}
                {totalDeliverables > 0 && (
                    <div className={`flex items-center gap-2.5 p-3 rounded-xl border text-xs font-bold transition-all ${
                        allChecked
                            ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-200 border-emerald-300 dark:border-emerald-800'
                            : 'bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-200 border-amber-300 dark:border-amber-800'
                    }`}>
                        {allChecked ? (
                            <>
                                <CheckCircleOutlined className="text-emerald-600 text-sm shrink-0" />
                                <span>
                                    All <strong>{totalDeliverables} deliverables verified</strong> ✓ Ready to mark milestone as completed.
                                </span>
                            </>
                        ) : (
                            <>
                                <InfoCircleOutlined className="text-amber-600 text-sm shrink-0" />
                                <span>
                                    Please check off all deliverables before completing: <strong>{completedDeliverables} of {totalDeliverables} verified</strong> ({totalDeliverables - completedDeliverables} remaining).
                                </span>
                            </>
                        )}
                    </div>
                )}

                {/* Footer Buttons */}
                <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-5 py-2.5 rounded-xl border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-bold text-sm hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                    >
                        Cancel
                    </button>
                    <Tooltip
                        title={
                            !canComplete
                                ? `Please check off all ${totalDeliverables} deliverables to unlock milestone completion.`
                                : ''
                        }
                    >
                        <span>
                            <button
                                type="button"
                                onClick={handleOk}
                                disabled={isCompleteDisabled}
                                className={`px-6 py-2.5 rounded-xl font-bold text-sm transition-all flex items-center gap-2 ${
                                    isCompleteDisabled
                                        ? 'bg-slate-200 dark:bg-slate-800 text-slate-400 dark:text-slate-500 border border-slate-300 dark:border-slate-700 cursor-not-allowed opacity-60 shadow-none'
                                        : 'text-white shadow-md hover:shadow-lg cursor-pointer transform active:scale-95'
                                }`}
                                style={
                                    !isCompleteDisabled
                                        ? { background: 'linear-gradient(90deg, #03488A 0%, #00AAF1 100%)' }
                                        : {}
                                }
                            >
                                <CheckCircleOutlined />
                                {loading ? 'Completing…' : 'Mark as Completed'}
                            </button>
                        </span>
                    </Tooltip>
                </div>
            </div>
        </Modal>
    );
}
